import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtMoney, fmtDate, todayInput } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Printer, ChevronRight, FileText, Save } from "lucide-react";

const emptyLine = () => ({ _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, description: "", amount: "" });

export default function InvoiceCreate() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({
    client_name: "",
    client_address: "",
    claim_number: 1,
    invoice_date: todayInput(),
    due_date: "",
    notes: "",
    lines: [emptyLine()],
    gst_included: true,
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, iRes] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/projects/${id}/invoices`),
        ]);
        setProject(pRes.data);
        setInvoices(iRes.data);
        setForm((f) => ({
          ...f,
          client_name: f.client_name || pRes.data.client_name || "",
          client_address: f.client_address || pRes.data.address || "",
          claim_number: (iRes.data?.length || 0) + 1,
        }));
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, [id]);

  const subtotal = form.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const net = form.gst_included ? subtotal / 1.1 : subtotal;
  const gst = form.gst_included ? subtotal - net : subtotal * 0.1;
  const total = form.gst_included ? subtotal : subtotal + gst;

  function addLine() { setForm({ ...form, lines: [...form.lines, emptyLine()] }); }
  function updateLine(i, patch) {
    const lines = form.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    setForm({ ...form, lines });
  }
  function removeLine(i) {
    if (form.lines.length === 1) return;
    setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  }

  async function save(e) {
    e.preventDefault();
    if (form.lines.some((l) => !l.description || !l.amount)) {
      toast.error("Fill in all line items");
      return;
    }
    setSaving(true);
    try {
      const body = {
        client_name: form.client_name,
        client_address: form.client_address,
        claim_number: Number(form.claim_number) || 1,
        invoice_date: new Date(form.invoice_date).toISOString(),
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        notes: form.notes,
        gst_included: form.gst_included,
        lines: form.lines.map((l) => ({ description: l.description, amount: Number(l.amount) })),
      };
      const { data } = await api.post(`/projects/${id}/invoices`, body);
      toast.success(`Invoice ${data.invoice_number} created`);
      setCreated(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (!project) return <div className="text-slate-500">Loading…</div>;

  if (created) return <InvoicePreview invoice={created} onNew={() => { setCreated(null); setForm((f) => ({ ...f, claim_number: (f.claim_number || 0) + 1, lines: [emptyLine()], notes: "" })); }} onBack={() => nav(`/projects/${id}`)} />;

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2 text-sm">
        <Link to={`/projects/${id}`} className="text-slate-500 hover:text-brandblue inline-flex items-center gap-1" data-testid="link-back-project">
          <ArrowLeft className="h-4 w-4" /> {project.name}
        </Link>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <span className="text-slate-700 font-medium">New progress-claim invoice</span>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Progress claim</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Create client invoice</h1>
        <p className="text-sm text-slate-500 mt-1.5">Bill your homeowner or lender for progress on <span className="font-semibold">{project.name}</span>.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form id="invoice-form" onSubmit={save} className="lg:col-span-2 space-y-6" data-testid="invoice-form">
          <Card className="p-6 border border-border shadow-sm space-y-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Client details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Client name" required>
                <Input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="h-12" data-testid="invoice-client-name" />
              </Field>
              <Field label="Progress claim number" required>
                <Input type="number" min="1" required value={form.claim_number} onChange={(e) => setForm({ ...form, claim_number: e.target.value })} className="h-12" data-testid="invoice-claim-number" />
              </Field>
            </div>
            <Field label="Client address">
              <Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} className="h-12" data-testid="invoice-client-address" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Invoice date" required>
                <Input type="date" required value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="h-12" data-testid="invoice-date" />
              </Field>
              <Field label="Due date">
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-12" data-testid="invoice-due-date" />
              </Field>
            </div>
          </Card>

          <Card className="p-6 border border-border shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Line items</div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Amounts include GST</span>
                <Switch checked={form.gst_included} onCheckedChange={(v) => setForm({ ...form, gst_included: v })} data-testid="invoice-gst-toggle" />
              </div>
            </div>
            <div className="space-y-3">
              {form.lines.map((l, i) => (
                <div key={l._key} className="grid grid-cols-12 gap-2" data-testid={`invoice-line-${i}`}>
                  <div className="col-span-12 md:col-span-8">
                    <Input required placeholder="Description (e.g. Progress Claim 3 — Framing complete)" value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} className="h-12" data-testid={`invoice-line-desc-${i}`} />
                  </div>
                  <div className="col-span-9 md:col-span-3">
                    <Input required type="number" step="0.01" min="0" placeholder="Amount" value={l.amount} onChange={(e) => updateLine(i, { amount: e.target.value })} className="h-12" data-testid={`invoice-line-amount-${i}`} />
                  </div>
                  <div className="col-span-3 md:col-span-1 flex items-center justify-end">
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(i)} disabled={form.lines.length === 1} className="h-10 w-10 text-slate-400 hover:text-status-danger-text" data-testid={`btn-remove-line-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" onClick={addLine} className="h-11" data-testid="btn-add-line">
              <Plus className="h-4 w-4" /> Add line
            </Button>
          </Card>

          <Card className="p-6 border border-border shadow-sm">
            <Field label="Notes / payment instructions">
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="e.g. Please pay within 14 days. BSB 123-456 Acct 12345678." data-testid="invoice-notes" />
            </Field>
          </Card>
        </form>

        <div>
          <Card className="p-6 border border-border shadow-sm sticky top-24">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Summary</div>
            <div className="space-y-2 text-sm">
              <Row label="Subtotal (ex GST)" value={fmtMoney(net, true)} />
              <Row label="GST (10%)" value={fmtMoney(gst, true)} />
              <div className="h-px bg-border my-2" />
              <Row label="Total (AUD)" value={fmtMoney(total, true)} bold />
            </div>
            <Button type="submit" form="invoice-form" onClick={save} disabled={saving} className="w-full h-12 mt-6 bg-brandblue hover:bg-brandblue-hover text-white text-base" data-testid="btn-save-invoice">
              <Save className="h-4 w-4" /> {saving ? "Creating…" : "Create invoice"}
            </Button>

            {invoices.length > 0 && (
              <div className="mt-6 pt-6 border-t border-border">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Previous invoices ({invoices.length})</div>
                <ul className="space-y-1.5 text-xs">
                  {invoices.slice(0, 5).map((inv) => (
                    <li key={inv.id}>
                      <Link to={`/invoices/${inv.id}`} className="flex justify-between hover:text-brandblue" data-testid={`prev-invoice-${inv.id}`}>
                        <span>{inv.invoice_number}</span>
                        <span className="font-semibold">{fmtMoney(inv.total, true)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function InvoicePreview({ invoice, onNew, onBack }) {
  return (
    <div className="space-y-6 print:space-y-0">
      <div className="flex items-center justify-between gap-3 print:hidden">
        <div className="flex items-center gap-2 text-sm">
          <button onClick={onBack} className="text-slate-500 hover:text-brandblue inline-flex items-center gap-1" data-testid="link-back-project">
            <ArrowLeft className="h-4 w-4" /> Back to project
          </button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onNew} className="h-11" data-testid="btn-new-invoice-again">
            <FileText className="h-4 w-4" /> Create another
          </Button>
          <Button onClick={() => window.print()} className="h-11 bg-brandblue hover:bg-brandblue-hover text-white" data-testid="btn-print-invoice">
            <Printer className="h-4 w-4" /> Print / PDF
          </Button>
        </div>
      </div>
      <InvoiceDoc inv={invoice} />
      <style>{`@media print { @page { size:A4; margin:12mm } body { background:white } nav, header.sticky, .print\\:hidden { display:none !important } main { padding:0 !important; max-width:100% !important } }`}</style>
    </div>
  );
}

export function InvoiceDoc({ inv }) {
  return (
    <div className="bg-white border border-border rounded-lg p-8 md:p-12 print:border-none print:p-0 print:shadow-none" data-testid="invoice-doc">
      <header className="flex items-start justify-between mb-8 pb-6 border-b border-border">
        <div>
          <div className="font-display text-2xl font-bold text-navy">BuildTrack</div>
          <div className="text-sm text-slate-600 mt-1">Issued by {inv.issuer_name}</div>
          <div className="text-xs text-slate-500">{inv.issuer_email}</div>
        </div>
        <div className="text-right">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Tax invoice</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="invoice-number">{inv.invoice_number}</div>
          <div className="text-xs text-slate-500 mt-1">Progress claim #{inv.claim_number}</div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Billed to</div>
          <div className="font-semibold text-slate-900 mt-1">{inv.client_name}</div>
          {inv.client_address && <div className="text-sm text-slate-600 whitespace-pre-line">{inv.client_address}</div>}
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Project</div>
          <div className="font-semibold text-slate-900 mt-1">{inv.project_name}</div>
          {inv.project_address && <div className="text-sm text-slate-600">{inv.project_address}</div>}
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Dates</div>
          <div className="text-sm text-slate-700 mt-1">Issued: {fmtDate(inv.invoice_date)}</div>
          {inv.due_date && <div className="text-sm text-slate-700">Due: {fmtDate(inv.due_date)}</div>}
        </div>
      </div>

      <table className="w-full text-sm mb-8" data-testid="invoice-lines-table">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="py-3 pr-2">Description</th>
            <th className="py-3 pl-2 text-right w-40">Amount (AUD)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {inv.lines.map((l, i) => (
            <tr key={`${inv.invoice_number}-line-${i}-${l.description?.slice(0, 20) || ""}`}>
              <td className="py-3 pr-2">{l.description}</td>
              <td className="py-3 pl-2 text-right font-semibold">{fmtMoney(l.amount, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end mb-8">
        <div className="w-full md:w-80 space-y-1.5">
          <Row label="Subtotal (ex GST)" value={fmtMoney(inv.subtotal_net, true)} />
          <Row label="GST (10%)" value={fmtMoney(inv.gst, true)} />
          <div className="h-px bg-border my-2" />
          <Row label="Total (AUD)" value={fmtMoney(inv.total, true)} bold big />
        </div>
      </div>

      {inv.notes && (
        <div className="p-4 bg-slate-50 border border-border rounded-md text-sm text-slate-700 whitespace-pre-wrap">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Notes</div>
          {inv.notes}
        </div>
      )}

      <footer className="mt-8 pt-6 border-t border-border text-xs text-slate-500 flex justify-between">
        <span>Generated by BuildTrack</span>
        <span>All amounts in AUD · ABN not provided</span>
      </footer>
    </div>
  );
}

function Row({ label, value, bold, big }) {
  return (
    <div className={`flex justify-between ${big ? "text-lg" : "text-sm"}`}>
      <span className={bold ? "font-semibold" : "text-slate-600"}>{label}</span>
      <span className={`font-display ${bold ? "font-bold text-slate-900" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">
        {label} {required && <span className="text-status-danger-text">*</span>}
      </Label>
      {children}
    </div>
  );
}
